import React from 'react';
import tw, { styled } from 'twin.macro';
import { Trash, DotsSixVertical, Clock } from '@phosphor-icons/react';
import { VideoScene } from './types';

interface SceneCardProps {
  scene: VideoScene;
  index: number;
  isSelected: boolean;
  isDragging?: boolean;
  onClick: () => void;
  onRemove: () => void;
  dragHandleProps?: any;
}

const CardWrapper = styled.div<{
  isSelected: boolean;
  isDragging: boolean;
}>`
  ${tw`relative flex-shrink-0 w-44 rounded-lg border bg-white cursor-pointer transition-all duration-200 overflow-hidden`}
  ${tw`hover:shadow-md`}
  ${({ isSelected }) =>
    isSelected && tw`ring-[3px] ring-indigo-500 border-transparent`}
  ${({ isDragging }) => isDragging && tw`shadow-xl opacity-90 rotate-2`}
`;

const ImageContainer = tw.div`relative w-full aspect-square bg-gray-100 overflow-hidden`;

const SceneImage = tw.img`w-full h-full object-cover`;

const IndexBadge = tw.span`absolute top-2 left-2 bg-black/60 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center`;

const RemoveButton = styled.button`
  ${tw`absolute top-2 right-2 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 transition-opacity hover:bg-red-500`}
  ${CardWrapper}:hover & {
    ${tw`opacity-100`}
  }
`;

const TransitionBadge = tw.span`absolute bottom-2 right-2 bg-white/90 text-gray-600 text-[10px] font-medium px-1.5 py-0.5 rounded`;

const ContentArea = tw.div`p-3`;

const Headline = tw.p`text-sm font-medium text-gray-800 leading-tight line-clamp-2`;

const MetaRow = tw.div`flex items-center gap-1 mt-1.5 text-gray-400`;

const Duration = tw.span`text-xs text-gray-500`;

const DragHandle = tw.div`flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing py-1`;

const SceneCard: React.FC<SceneCardProps> = ({
  scene,
  index,
  isSelected,
  isDragging = false,
  onClick,
  onRemove,
  dragHandleProps,
}) => {
  return (
    <CardWrapper
      isSelected={isSelected}
      isDragging={isDragging}
      onClick={onClick}
    >
      <DragHandle {...dragHandleProps}>
        <DotsSixVertical size={16} weight="bold" />
      </DragHandle>

      <ImageContainer>
        <SceneImage
          src={scene.croppedPreviewUrl || scene.imageUrl}
          alt={scene.headline}
          loading="lazy"
        />
        <IndexBadge>{index + 1}</IndexBadge>
        <RemoveButton
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title="Remove scene"
        >
          <Trash size={14} weight="bold" />
        </RemoveButton>
        <TransitionBadge>{scene.transition}</TransitionBadge>
      </ImageContainer>

      <ContentArea>
        <Headline>{scene.headline}</Headline>
        <MetaRow>
          <Clock size={12} />
          <Duration>{scene.duration}s</Duration>
        </MetaRow>
      </ContentArea>
    </CardWrapper>
  );
};

export default SceneCard;
